import { createSelector } from "@reduxjs/toolkit";

import { isNil } from "ramda";

import { IProfile } from "types/redux/userInterfaces";

import { AppState } from "../store";

const getUser = (state: AppState) => state.user;

export const getUserFirstAuthentication = createSelector(
 getUser,
 (user): boolean => !!user?.authenticated
);

export const getUserAuthenticated = createSelector(
 getUser,
 (user): boolean => !!user?.authenticated && !!user?.doubleAuthenticated
);

export const getIsAdmin = createSelector(getUser, (user): boolean => user?.isAdmin || false);

export const getUserPersonalData = createSelector(
 getUser,
 (user): { firstname: string; lastname: string; avatar: string | null; company: string } => ({
  firstname: user?.firstname || "firstname",
  lastname: user?.lastname || "lastname",
  avatar: user?.propic || null,
  company: user?.companyName || ""
 })
);
export const getUserBalance = createSelector(getUser, (user): number => user?.balance || 0);
export const getUserBonus = createSelector(getUser, (user): number => user?.bonus || 0);
export const getUserCash = createSelector(getUser, (user): number => user?.cash || 0);
export const getUserAvatar = createSelector(getUser, (user): string => user?.propic || "");
export const getUserTax = createSelector(getUser, (user): number => user?.tax || 0);
export const getUserId = createSelector(getUser, (user): number => user?.id || 0);
export const getUserIdString = createSelector(getUser, (user): string => user?.userid || "");
export const getTelegramId = createSelector(getUser, (user): string => user?.telegramID || "");
export const getEmail = createSelector(getUser, (user): string => user?.email || "");
export const get2FA = createSelector(getUser, (user): boolean => user?.twoWayAuth || false);
export const getIban = createSelector(getUser, (user): string | null => user?.iban || null);
export const getRidConfirm = createSelector(getUser, (user): boolean | null =>
 user?.rid_confirmed === null ? null : user?.rid_confirmed ? true : false
);
export const getRidDocumentVerified = createSelector(
 getUser,
 (user): boolean | null => user?.rid_document_verified || null
);
export const getRidDocument = createSelector(
 getUser,
 (user): string | null => user?.rid_document || null
);
export const getIdentityDocument = createSelector(
 getUser,
 (user): string | null => user?.rid_identity_card || null
);
export const getCfDocument = createSelector(
 getUser,
 (user): string | null => user?.rid_fc_card || null
);
export const getStripeAutopayment = createSelector(
 getUser,
 (user): number => user?.stripe_autopayment || 0
);
export const getStripeRechargeAmount = createSelector(
 getUser,
 (user): number | null => user?.stripe_recharge_amount || null
);
export const getStripeRechargeDate = createSelector(
 getUser,
 (user): string | null => user?.stripe_recharge_date || null
);
export const getRidBalanceLimit = createSelector(
 getUser,
 (user): number | null => user?.rid_balance_limit || null
);
export const getAmount = createSelector(getUser, (user): number | null => user?.rid_amount || null);
export const getBank = createSelector(getUser, (user): string | null => user?.rid_bank || null);
export const getRidDate = createSelector(getUser, (user): string | null => user?.rid_date || null);
export const getUserContract = createSelector(
 getUser,
 (user): boolean => user?.contract_accepted || false
);
export const getUserLanguage = createSelector(getUser, (user): string => {
 const storedLanguage = localStorage.getItem("language");
 return user && !isNil(user?.language) ? user.language : storedLanguage || "it";
});
export const getUser2FactorDisabled = createSelector(
 getUser,
 (user): boolean => !!user?.twoWayAuthDisabled
);
export const getUserProfileData = createSelector(
 getUser,
 (user): IProfile => ({
  firstname: user?.firstname || "",
  lastname: user?.lastname || "",
  companyName: user?.companyName || "",
  email: user?.email || "",
  phone: user?.telephone || null,
  street: user?.address || null,
  city: user?.city || null,
  country: user?.country || null,
  zipCode: user?.zip || null,
  vatCode: user?.partitaIVA || null,
  sdi: user?.sdi || null,
  pec: user?.pec || null
 })
);

export const getUserExternalLogin = createSelector(
 getUser,
 (user): { googleId: string | null; linkedinId: string | null; githubId: string | null } => ({
  googleId: user?.googleId || null,
  linkedinId: user?.linkedinId || null,
  githubId: user?.githubId || null
 })
);
